import { FunctionComponent, useMemo } from 'react';
import { IntlProvider } from 'react-intl-next';
import { QueryClientProvider } from '@tanstack/react-query';
import { Content } from '@atlaskit/page-layout';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import {
  Router,
  RouteComponent,
  createBrowserHistory,
  useRouter,
} from 'react-resource-router';
import { defaultLocale } from '@app/i18n/config';
import messages from '@app/i18n/messages/en.json';
import { initSentry } from '@app/sentry';

import { TopNavigationContents, Footer } from '@app/navigation';
import {
  appUpdatesListRoute,
  appUpdatesLegacyBundledChangesRoute,
  appUpdatesBundledChangesRoute,
  appUpdatesDetailsRoute,
} from './Routes';
import { AppUpdatesContextProvider, useAppUpdatesContext } from '@app/app';
import { queryClient, api } from './QueryClient';
import { useAuth, useUserContext } from '@app/auth';
import { useFeatureGatesReady } from '@app/feature-gates';
import { initUFO, useReactUFOWatcher } from '@app/ufo';
import { useUnreadPrune } from '@app/unread-indicator';

// Initialize Sentry for error tracking
initSentry();

// Initialize UFO performance monitoring
initUFO();

// Release tracks routes must precede the dynamic ':id' route so they aren't
// matched as release note ids.
const appRoutes = [
  appUpdatesListRoute,
  appUpdatesLegacyBundledChangesRoute,
  appUpdatesBundledChangesRoute,
  appUpdatesDetailsRoute,
];

// TODO: keeping for now, will remove once OneTrust is done
// console.log(process.env.REACT_APP_ONETRUST_SCRIPT_DOMAIN);

const fullHeightStyles = xcss({
  height: '100vh',
});

const mainStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  // @ts-ignore - need to fix styles
  gap: '112px',
  margin: '0 auto',
  width: '1232px',
  '@media (max-width: 1279px)': {
    gap: '72px',
    width: '100%',
    paddingInline: 'space.300',
  },
  '@media (max-width: 767px)': {
    gap: '64px',
    width: '100%',
    paddingInline: 'space.200',
  },
});

const AppContents: FunctionComponent = () => {
  useAuth();
  const { state } = useAppUpdatesContext();
  const [routerState] = useRouter();

  // Trigger a one-time, self-throttled prune of the unread read set on app load.
  useUnreadPrune();

  // Central UFO watcher — tracks page-load on initial load and transitions on
  // subsequent route changes. Called here (inside <Router>) so useRouter() works.
  // Individual pages should use <UFOSegment> and <UFOLoadHold> only.
  useReactUFOWatcher(routerState);
  const { isLoggedIn, loggedInUser } = state;

  return (
    <>
      <TopNavigationContents
        isLoggedIn={isLoggedIn}
        loggedInUser={loggedInUser}
      />
      <div id="core-root">
        <Content>
          <Box xcss={mainStyles}>
            <RouteComponent />
          </Box>
        </Content>
      </div>
      <footer>
        <Footer isLoggedIn={isLoggedIn} loggedInUser={loggedInUser} />
      </footer>
    </>
  );
};

export const App: FunctionComponent = () => {
  const history = useMemo(() => createBrowserHistory(), []);
  const isFeatureGatesReady = useFeatureGatesReady();
  const { user } = useUserContext();

  // Wait for Statsig to finish initializing before evaluating gates.
  // Without this guard, checkGate() returns false during async init,
  // which would incorrectly redirect users away from /release-notes.
  if (!isFeatureGatesReady) {
    return (
      <Flex justifyContent="center" alignItems="center" xcss={fullHeightStyles}>
        <Spinner size="large" interactionName="feature-gates-initializing" />
      </Flex>
    );
  }

  return (
    <IntlProvider locale={defaultLocale} messages={messages}>
      <AppUpdatesContextProvider api={api} initialState={{ user }}>
        <QueryClientProvider client={queryClient}>
          <Router routes={appRoutes} history={history}>
            <AppContents />
          </Router>
        </QueryClientProvider>
      </AppUpdatesContextProvider>
    </IntlProvider>
  );
};
